/**
 * Copyright © 2026 Adnuntius AS.
 */
import {firstValueFrom} from 'rxjs';
import {Component, Inject, Input, OnInit} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {PopoverModule} from 'ngx-bootstrap/popover';
import * as _ from 'lodash';

import template from './query-column-selector.component.html';
import {OBJECT_TYPE} from '../constants/object-type';
import {AdnLogService} from '../../../components/util/adn-log.service';
import {UserProfileApiService} from '../../admin/user-profile/user-profile-api.service';
import {ChartOptionsUtil} from '../options/chart-options-util.service';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {APP_SHARED} from '../app-shared';

@Component({
  standalone: true,
  imports: [...APP_SHARED, FormsModule, PopoverModule],
  selector: 'adn-query-column-selector',
  template
})
export class QueryColumnSelectorComponent implements OnInit {
  @Input() primaryType = '';
  @Input() secondaryType = '';
  @Input() secondaryTypeDirect = '';
  @Input() hideStats: any;
  @Input() showCustom: any;
  @Input() sel: any;
  @Input() changeHook: any;
  @Input() chartOptionsProfileKey = '';

  availableFields: any[] = [];
  hideStatsValue = false;

  private allFields: any[] = [];
  private readonly customField = {id: 'custom', name: 'Custom Fields', text: 'Custom Fields', key: 'custom'};

  constructor(
    private readonly adnLog: AdnLogService,
    @Inject(ChartOptionsUtil) private readonly chartOptionsUtil: any,
    @Inject(UserProfileApiService) private readonly UserProfile: UserProfileApiService,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    @Inject(LocalNetworkProfile) private readonly localNetworkProfile: any
  ) {
  }

  ngOnInit(): void {
    this.sel = this.sel || {};
    this.hideStatsValue = this.coerceBoolean(this.hideStats);

    const chartOptionObject = OBJECT_TYPE[this.primaryType];
    const groupByObject = OBJECT_TYPE[this.secondaryType];
    const fieldKeyPrefix = _.get(groupByObject, ['apiParam'], this.secondaryTypeDirect);
    const chartOptionsFieldsKey = fieldKeyPrefix ? fieldKeyPrefix + 'Fields' : 'totalsFields';
    const chartOptions = this.chartOptionsUtil.getOptions(chartOptionObject.caps);

    this.allFields = _.filter(chartOptions[chartOptionsFieldsKey], (field: any) => {
      return !field.availability
        || (this.localNetworkProfile.isPureMarketplacePlatform() && field.availability.MARKETPLACE)
        || (!this.localNetworkProfile.isPureMarketplacePlatform() && field.availability.DIRECT);
    });

    this.availableFields = _.cloneDeep(this.allFields);
    if (this.coerceBoolean(this.showCustom)) {
      this.availableFields.push(this.customField);
    }

    const profileParam = this.resolveProfileParam(groupByObject, chartOptions);
    if (!profileParam) {
      this.adnLog.warn('Missing a good profile param for query column selector.');
      this.sel.availableFields = _.map(_.filter(this.availableFields, (field: any) => {
        return !field.unselected;
      }), 'key');
      this.sel.fullText = false;
      this.sel.allFields = false;
      this.syncSelectedFields();
      return;
    }

    const profile = this.localUserProfile.get().profile;
    const profileSelFieldIds = _.get(profile, [profileParam, 'selFieldIds']) || [];
    const profileFullText = _.get(profile, [profileParam, 'fullText']) || false;
    const profileAllFields = _.get(profile, [profileParam, 'allFields']) || false;

    this.sel.fullText = profileFullText;
    this.sel.allFields = profileAllFields;
    this.sel.availableFields = profileSelFieldIds.length
      ? _.filter(profileSelFieldIds, (fieldKey: string) => this.fieldExists(fieldKey))
      : _.map(_.filter(this.availableFields, (field: any) => {
        return !field.unselected;
      }), 'key');

    this.syncSelectedFields();
  }

  isFieldSelected(fieldKey: string): boolean {
    return this.sel.availableFields.indexOf(fieldKey) > -1;
  }

  onFieldSelectionChange(fieldKey: string, checked: boolean): void {
    const availableFieldKeys = new Set(this.sel.availableFields);
    if (checked) {
      availableFieldKeys.add(fieldKey);
    } else {
      availableFieldKeys.delete(fieldKey);
    }
    this.sel.availableFields = _.map(_.filter(this.availableFields, (field: any) => {
      return availableFieldKeys.has(field.key);
    }), 'key');
    this.updateFields();
  }

  onFullTextChange(fullText: boolean): void {
    this.sel.fullText = fullText;
    this.updateFields();
  }

  onAllFieldsChange(allFields: boolean): void {
    this.sel.allFields = allFields;
    this.updateFields();
  }

  private fieldExists(fieldKey: string): boolean {
    return _.some(this.availableFields, {key: fieldKey});
  }

  private resolveProfileParam(groupByObject: any, chartOptions: any): string {
    if (this.chartOptionsProfileKey) {
      return _.get(chartOptions, this.chartOptionsProfileKey, this.chartOptionsProfileKey);
    }
    return groupByObject ? groupByObject.apiParam + 'RowsOptions' : '';
  }

  private syncSelectedFields(): void {
    this.sel.fullFields = _.filter(this.allFields, (field: any) => {
      return this.sel.availableFields.indexOf(field.key) > -1;
    });

    if (this.sel.availableFields.indexOf(this.customField.key) > -1) {
      this.sel.fullFields.push(this.customField);
    }

    if (_.isFunction(_.get(this.changeHook, 'fieldChange'))) {
      this.changeHook.fieldChange();
    }
  }

  private updateFields(): void {
    const chartOptionObject = OBJECT_TYPE[this.primaryType];
    const groupByObject = OBJECT_TYPE[this.secondaryType];
    const chartOptions = this.chartOptionsUtil.getOptions(chartOptionObject.caps);
    const profileParam = this.resolveProfileParam(groupByObject, chartOptions);

    if (!profileParam) {
      this.adnLog.warn("Can't save settings because of a faulty profileParam.");
      this.syncSelectedFields();
      return;
    }

    const localUser = this.localUserProfile.get();
    const profile = localUser.profile;
    profile[profileParam] = {
      selFieldIds: this.sel.availableFields,
      fullText: this.sel.fullText || false,
      allFields: this.sel.allFields || false
    };

    firstValueFrom(this.UserProfile.saveProfile({
      id: localUser.userId,
      profile: _.pick(profile, [profileParam])
    })).then((apiUser: any) => {
      this.localUserProfile.safeSaveProfile(profileParam, apiUser.profile[profileParam]);
    });

    this.syncSelectedFields();
  }

  private coerceBoolean(value: any): boolean {
    return value === '' || value === true || value === 'true';
  }
}
